import React from "react";
import { useParams } from "react-router-dom";
import FivePhdImage from "../../images/Five_PhD_Application_Tips.png";
import FiveMistakes from "../../images/Five_Common_Mistakes.png";
import HowToApply from "../../images/How_To_Apply.png";
import SixBooks from "../../images/Six_Books_For_PhD.png";
import StudentVisaPhD from "../../images/PhD_Visas.png";
import styles from "./BlogPost.module.css";

const colors = {
  mainBlue: "#061941",
  isabelline: "#F4F3EE",
  xanthous: "#F2AF29",
};

// String inside "" must match the slug generated in BlogArticles.js
// For example in BlogArticles.js: title "5 Phd Applications"
// Then string should be "5-phd-applications"

const blogContent = {
  "5-phd-application-tips": {
    title: "5 PhD Application Tips",
    author: "Landon Amaba",
    readTime: "3 min read",
    thumbnail: FivePhdImage,
    content: `
      <h1>Five Application Tips for Higher Education</h1>
      <div class="${styles.intro}">
        <p>
          The application for higher education, whether a PhD or Masters, is often challenging, primarily due to lack of knowledge. Many unsuccessful applications result from a communication gap between students and professors. This knowledge gap can usually be bridged by researching potential supervisors. In order to apply for a PhD program, you will need to know—and show—that you are the best applicant for an advertised position by proposing a research project you designed yourself. The average time to complete a PhD depends on the program; for example, it is around four years for the majority of engineering branches. Below are five tips to help you secure financial support from a university and start the dream of getting a PhD.
        </p>
      </div>
      <div class="${styles.tip}">
        <h2>Tip 1: Prepare Yourself</h2>
        <p>
          Deciding on higher education and going abroad requires a tremendous amount of courage. It is always tempting to postpone higher education until you have more experience, but you may lose precious years debating this decision.
        </p>
        <p class="${styles.recommendation}"><strong>Recommendation:</strong> Start preparing for higher education (and study abroad) by the end of the first year of your undergraduate study. This will give you enough time to prepare a proper application and secure financial support. Understanding <strong>why</strong> you want a PhD is the primary step. Sooner is better.</p>
      </div>
      <div class="${styles.tip}">
        <h2>Tip 2: Prepare a Research Statement</h2>
        <p>
          Once you decide to pursue higher education, it is extremely important to let others know your research interests. This is where the research statement comes in. A research statement contains the key aspects of your research interests. At this point, it may be hard for you to know all research directions, because most undergraduate classes are meant for building your fundamental knowledge. The best way to learn about current research trends is to follow scientific magazines (such as IEEE Spectrum for Electrical and Computer Engineering). Once you identify your interest, connect it with your undergraduate courses and class projects. A research statement informs hiring committees about your interests, your ability to do original work, and why your work is important.
        </p>
        <p class="${styles.recommendation}"><strong>Recommendation:</strong> The aim of a research statement is to introduce yourself to a committee—which may include professors inside and outside your specialty—and persuade them that you are informed and the best person to perform the research. Keep it detailed and straight to the point, and always use formal language.</p>
      </div>
      <div class="${styles.tip}">
        <h2>Tip 3: Contact Professors</h2>
        <p>
          Contacting professors and getting responses from them might be the most crucial step in your PhD applications. Once you have your research statement, contact different professors immediately. In the United States and many EU countries, professors directly support graduate students and have absolute authority over whom they select. It is vital to impress the professors. Prepare a brief email highlighting your research interest, university, GPA, GRE scores, etc. Once you receive a response, you can share additional information such as a resume, research statement, transcripts, and projects. You can also request an online meeting (Zoom, MS Teams, etc.) to further explain your expertise.
        </p>
        <p class="${styles.recommendation}"><strong>Recommendation:</strong> Contact a list of professors under whom you can grow. Sooner is always better; however, ensure you have enough material on your resume. NeuHawk can introduce you to many professors who are looking for motivated students.</p>
      </div>
      <div class="${styles.tip}">
        <h2>Tip 4: Acquire Proper Letters of Recommendation</h2>
        <p>
          Applicants are generally required to submit up to three recommendation letters from academia or industry professionals who have worked with them. These letters provide your future professors with insight into your personality and knowledge. They might not know you, but there is a high possibility they know your recommender. Make every effort to build excellent relationships with your recommendation providers.
        </p>
        <p class="${styles.recommendation}"><strong>Recommendation:</strong> Recommendations should be written by people who know you professionally. Most letters should come from your current or previous university(ies). Your recommendation letters provide a potential supervisor with reasons why you are a suitable candidate for higher education.</p>
      </div>
      <div class="${styles.tip}">
        <h2>Tip 5: Work Hard for a Decent GRE Score</h2>
        <p>
          The GRE (Graduate Record Examination) is a standardized exam by ETS and is a mandatory requirement for admission to most U.S. universities. The score is valid for five years, so you can take the exam early, but ensure you are prepared. The GRE score is one of the key parameters graduate schools look for, and applications with low GRE scores are often discarded.
        </p>
        <p class="${styles.recommendation}"><strong>Recommendation:</strong> A good GRE score gives professors confidence in your abilities and gives you an edge over other applicants with lower scores. Take several mock tests before the actual GRE exam, evaluate yourself, and address your weaknesses.</p>
      </div>
    `,
  },
  "graduate-school-5-common-misakes": {
    title: "Graduate School: 5 Common Mistakes",
    author: "Landon Amaba",
    readTime: "4 min read",
    thumbnail: FiveMistakes,
    content: `
      <h1>Graduate School: 5 Common Mistakes</h1>
      <div class="${styles.intro}">
        <p>
          Preparing for a solid application with a strong research statement, acquiring proper letters of recommendation, and working hard for a decent GRE score is a long process. Since applicants spend much time throughout this process, they tend to make mistakes without realizing it. Below are the most common mistakes you might encounter and our recommendations may help you to avoid them:
        </p>
      </div>
      <div class="${styles.tip}">
        <h2>1: More the applications, better the chance.</h2>
        <p>
          You might think that applying to a large number of schools will provide you with better success. Unfortunately, by doing so, you end up spending more time (and application fee, which may be significant) in applying to schools. Keep in mind that you need to ask for recommendation letters to complete your application. It might give a negative impression to your recommenders as well.
        </p>
        <p class="${styles.recommendation}"><strong>Recommendation:</strong> Keep your applications limited to a small number of schools. Make sure that you follow our five application tips for preparing a strong application.</p>
      </div>
      <div class="${styles.tip}">
        <h2>2: Apply after gaining some experience.</h2>
        <p>
          Like you, the majority of the students may think that it is better to pursue higher education after some industry experience (say for two years working in a company). There is nothing wrong with joining a Ph.D. program from Industry. Unfortunately, it becomes exceptionally difficult to come back to school.
        </p>
        <p class="${styles.recommendation}"><strong>Recommendation:</strong> Apply for a Ph.D. program when just you're at school and join higher education just after obtaining your bachelor's degree.</p>
      </div>
      <div class="${styles.tip}">
        <h2>3: Apply without contacting professors.</h2>
        <p>
          Professors play a vital role in the graduate admission process. Unfortunately, it is extremely challenging to evaluate all the candidates and select the one that matches the criteria, which might vary widely from person to person. Contacting a professor before the application deadline provides ample time to evaluate your research interest, resume, and research statement.
        </p>
        <p class="${styles.recommendation}"><strong>Recommendation:</strong> The majority of the Ph.D. students are supported by their professors. It would be best if you got some assurance of financial support from the professor with whom you plan to work in the future before applying to a school. Hence, our three recommendations before application are contact, contact and contact to the professor.</p>
      </div>
      <div class="${styles.tip}">
        <h2>4: Prepare application materials by professionals</h2>
        <p>
          It is a common notion that preparing the research statement with the help of a professional strengthens the application. Unfortunately, the professors can quickly identify whether you prepare it by yourself or someone prepared for you. Some professors may chat with you using Zoom or some other media to evaluate you. Some professors may value more on your integrity more than your knowledge.
        </p>
        <p class="${styles.recommendation}"><strong>Recommendation:</strong> Prepare all your application material by yourself. You might seek help from professionals to evaluate your current state. However, never use any material which you do not prepare.</p>
      </div>
      <div class="${styles.tip}">
        <h2>5: Attempt to wing an interview.</h2>
        <p>
          Going unprepared for an interview is the beginning of a recipe for failure. An interview, especially for the first time, is when you get to introduce yourself and your interests to a potential supervisor. As an applicant, you should know your goals and state of knowledge. Going into an interview and speaking off-topic due to lack of information or inadequate research puts you as an applicant in a wrong spot, telling your potential supervisor you have no idea what the program is about.
        </p>
        <p class="${styles.recommendation}"><strong>Recommendation:</strong> One of the most important parts of your application to a Ph.D. program is the interview. Being prepared ahead of time makes you feel in control, calm, and collected as an applicant. Staying calm and collected makes your potential supervisor see you are prepared for whatever comes your way with regards to potentially getting accepted into their program.</p>
      </div>
    `,
  },
  "graduate-school-how-to-apply": {
  title: "Graduate School: How to Apply",
  author: "Landon Amaba",
  readTime: "2 min read",
  thumbnail: HowToApply,
  content: `
    <h1>Graduate School: How to Apply</h1>
    <div class="${styles.intro}">
      <p>Starting the journey toward a PhD is both exciting and challenging. Applying to graduate school requires careful planning, strong preparation, and clear goals. While the process can feel overwhelming at first, breaking it into steps makes it much more manageable. Here’s a concise guide to help students who are beginning to think about and prepare for doctoral applications—globally, with a particular focus on the U.S. system.</p>
    </div>
    <div class="${styles.tip}">
      <h2>Step 1: Research Schools and Programs</h2>
      <p>The foundation of a successful PhD application is finding the right fit. Start by identifying programs that align with your academic interests, preferred methodologies, and long-term career goals.</p>
      <ul>
        <li><strong>Match research interests:</strong> Review faculty profiles, lab websites, and recent publications.</li>
        <li><strong>Consider program strengths:</strong> Some schools are strong in certain subfields or research approaches.</li>
        <li><strong>Check requirements:</strong> Admission expectations vary. Some programs require standardized tests (like the GRE), while others do not.</li>
      </ul>
    </div>
    <div class="${styles.tip}">
      <h2>Step 2: Prepare Application Materials</h2>
      <p>Most applications include the following:</p>
      <ul>
        <li><strong>Curriculum Vitae (CV)/Resume:</strong> Highlight academic achievements, research experience, and relevant skills.</li>
        <li><strong>Statement of Purpose/Research Statement:</strong> Describe your research interests, past projects, and why you are applying to the program.</li>
        <li><strong>Letters of Recommendation:</strong> Secure strong endorsements from professors or research supervisors who know your work.</li>
        <li><strong>Transcripts:</strong> Provide academic records as required.</li>
      </ul>
      <p><em>Tip:</em> Begin drafting materials early. Strong applications often go through several rounds of revisions.</p>
    </div>
    <div class="${styles.tip}">
      <h2>Step 3: Contact Potential Advisors</h2>
      <p>PhD training is deeply tied to mentorship. Before applying, reach out to faculty whose work excites you.</p>
      <ul>
        <li><strong>Be professional and concise:</strong> Introduce yourself, mention shared research interests, and ask if they are taking students.</li>
        <li><strong>Demonstrate preparation:</strong> Refer to their specific projects or publications.</li>
        <li><strong>Be respectful:</strong> Faculty may be busy or unable to accept new students; a polite email still leaves a positive impression.</li>
      </ul>
    </div>
    <div class="${styles.tip}">
      <h2>Step 4: Submit Applications</h2>
      <p>Once your materials are ready, complete the submission process carefully:</p>
      <ul>
        <li><strong>Track deadlines:</strong> They vary widely by school and program.</li>
        <li><strong>Check required documents:</strong> Some systems won’t allow updates after submission.</li>
        <li><strong>Budget for fees:</strong> Application fees can add up; some schools offer waivers.</li>
      </ul>
      <p>Submitting early (when possible) helps avoid last-minute technical issues.</p>
    </div>
    <div class="${styles.conclusion}">
      <p>Applying to graduate school is a major milestone. By researching programs, preparing strong materials, reaching out to potential advisors, and managing applications on time, you’ll position yourself for success. Remember: a PhD is not only about the school—it’s about finding the right academic environment and mentorship to grow as a researcher.</p>
    </div>
  `,
},
"6-books-every-phd-student-should-read": {
  title: "6 Essential Books Every PhD Student Should Read",
  author: "Landon Amaba",
  readTime: "4 min read",
  thumbnail: SixBooks,
  content: `
    <h1>6 Essential Books Every PhD Student Should Read</h1>
    <div class="\${styles.intro}">
      <p>Starting a PhD isn’t just about research—it’s about learning how to navigate academia, stay productive, and keep writing even when motivation runs low. Thankfully, there are some fantastic resources out there that can make the journey easier. Here are six books that every doctoral student should consider adding to their toolkit.</p>
    </div>

    <div class="\${styles.tip}">
      <h2>1. The Professor Is In (Karen Kelsky)</h2>
      <p>A brutally honest guide to the academic job market. Kelsky offers practical strategies on everything from crafting a strong CV to acing interviews. It’s not always an easy read, but it’s essential if you plan to pursue a career in academia.</p>
    </div>

    <div class="\${styles.tip}">
      <h2>2. Good Work If You Can Get It (James Brennan)</h2>
      <p>Brennan provides an insider’s perspective on the realities of academic life. The book highlights both the joys and the challenges, making it a thoughtful resource for those considering whether academia is the right long-term path.</p>
    </div>

    <div class="\${styles.tip}">
      <h2>3. The Field Guide to Grad School (Jessica Calarco)</h2>
      <p>Think of this as the roadmap every graduate student wishes they had on day one. Calarco covers choosing committees, managing feedback, publishing, and more—all in a clear, approachable style that makes grad school feel less like a maze.</p>
    </div>

    <div class="\${styles.tip}">
      <h2>4. How to Write a Lot (Paul Silvia)</h2>
      <p>Writing regularly is one of the hardest parts of a PhD. Silvia’s concise book offers strategies for building consistent writing habits and making progress without burning out. Short, practical, and highly motivating.</p>
    </div>

    <div class="\${styles.tip}">
      <h2>5. Write No Matter What (Joli Jensen)</h2>
      <p>While Silvia focuses on routines, Jensen addresses the emotional roadblocks of academic writing: perfectionism, impostor syndrome, and procrastination. It’s like having a mentor gently remind you that your words matter and your voice belongs in the conversation.</p>
    </div>

    <div class="\${styles.tip}">
      <h2>6. Deep Work (Cal Newport)</h2>
      <p>Although not written specifically for academics, Newport’s productivity classic is especially relevant for researchers. He makes the case for distraction-free focus and provides strategies for carving out time to do meaningful, high-level work.</p>
    </div>

    <div class="\${styles.conclusion}">
      <p>Each of these books tackles a different aspect of the PhD journey—career planning, academic life, writing, and focus. Together, they form a powerful toolkit to help you not only survive but thrive in graduate school. Pick one or two that speak to your current challenges and keep the rest close by for when you need them most.</p>
    </div>
  `,
},

"student-visas-for-phds": {
  title: "How to Obtain a Student Visa for a PhD in the United States: Step-by-Step Guide",
  author: "Landon Amaba",
  readTime: "6 min read",
  thumbnail: StudentVisaPhD,
  content: `
    <h1>How to Obtain a Student Visa for a PhD in the United States: Step-by-Step Guide</h1>
    <div class="\${styles.intro}">
      <p>Pursuing a PhD in the United States is a dream for many students worldwide. Beyond academic excellence, understanding the visa process is essential for ensuring a smooth transition into U.S. graduate education. This guide walks you through the <strong>student visa process (F-1/J-1)</strong>, what happens after graduation, and clarifies common misconceptions.</p>
    </div>

    <div class="\${styles.tip}">
      <h2>Step 1: Secure Admission to a PhD Program</h2>
      <p>Before applying for a visa, you must be formally accepted into a U.S. university.</p>
      <ul>
        <li>Once admitted, the university will issue you either:
          <ul>
            <li><strong>Form I-20</strong> (for the F-1 student visa), or</li>
            <li><strong>Form DS-2019</strong> (for the J-1 exchange visitor visa).</li>
          </ul>
        </li>
        <li>These documents confirm your eligibility and are required to proceed with your visa application.</li>
      </ul>
    </div>

    <div class="\${styles.tip}">
      <h2>Step 2: Pay the SEVIS Fee</h2>
      <p>SEVIS (Student and Exchange Visitor Information System) tracks international students in the U.S.</p>
      <ul>
        <li>Pay the <strong>I-901 SEVIS fee</strong> online before your visa interview (currently $350 for F-1, $220 for J-1).</li>
        <li>Keep the receipt—it’s proof you’re registered in SEVIS.</li>
      </ul>
    </div>

    <div class="\${styles.tip}">
      <h2>Step 3: Complete the Visa Application (DS-160)</h2>
      <ul>
        <li>Fill out the <strong>DS-160 form</strong> online via the U.S. Department of State website.</li>
        <li>Upload a passport-style photo that meets requirements.</li>
        <li>Save the confirmation page with the barcode—you’ll need it for your interview.</li>
      </ul>
    </div>

    <div class="\${styles.tip}">
      <h2>Step 4: Schedule and Attend the Visa Interview</h2>
      <p>Book an appointment at your nearest U.S. consulate or embassy.</p>
      <p>Documents to bring include:</p>
      <ul>
        <li>Valid passport</li>
        <li>DS-160 confirmation</li>
        <li>SEVIS fee receipt</li>
        <li>I-20/DS-2019</li>
        <li>Financial evidence showing you can cover tuition and living expenses</li>
        <li>Admission letter from your university</li>
      </ul>
      <p>During the interview, you’ll be asked about your program, funding, and future plans.</p>
    </div>

    <div class="\${styles.tip}">
      <h2>Step 5: Receive Your Visa and Prepare for Travel</h2>
      <ul>
        <li>If approved, your passport will be returned with the F-1 or J-1 visa stamp.</li>
        <li>You may enter the U.S. <strong>up to 30 days before</strong> your program start date.</li>
      </ul>
    </div>

    <div class="\${styles.section}">
      <h2>After Graduation: What Happens Next?</h2>
      <p>Many PhD students wonder what their visa status means once their program ends. Here’s the breakdown:</p>

      <h3>✅ For F-1 Visa Holders</h3>
      <ul>
        <li><strong>Optional Practical Training (OPT):</strong> After graduation, you may apply for 12 months of OPT, which allows you to work in your field. STEM graduates (including most PhDs) can extend OPT for an additional 24 months.</li>
        <li><strong>Transition to H-1B:</strong> If you find an employer willing to sponsor you, you may switch to an H-1B work visa (though current policy changes, like fee increases, affect this pathway).</li>
        <li><strong>Other options:</strong> Some graduates move to O-1 visas (for individuals with extraordinary ability) or permanent residency via employer sponsorship.</li>
      </ul>

      <h3>✅ For J-1 Visa Holders</h3>
      <ul>
        <li>Many J-1 programs require a <strong>two-year home residency</strong> before you can return on another U.S. work visa.</li>
        <li>Waivers are possible but not guaranteed.</li>
      </ul>
    </div>

    <div class="\${styles.section}">
      <h2>Common Misconceptions</h2>
      <ul>
        <li><strong>“The student visa guarantees me a job after graduation.”</strong> ❌ False. The visa is for study. Work authorization (OPT, H-1B, O-1) requires separate applications.</li>
        <li><strong>“If my PhD is fully funded, I don’t need to show financial proof.”</strong> ❌ Incorrect. Even with funding, you must provide documentation (e.g., stipend letter, assistantship contract).</li>
        <li><strong>“A U.S. PhD automatically leads to permanent residency.”</strong> ❌ Not true. While a PhD strengthens green card applications (especially under EB-2 NIW or EB-1 categories), it is not automatic.</li>
        <li><strong>“J-1 visas are easier to get than F-1 visas.”</strong> ❌ Not always. J-1 visas may carry stricter restrictions after graduation (like the two-year return rule).</li>
      </ul>
    </div>

    <div class="\${styles.conclusion}">
      <h2>Big Picture: Why This Matters</h2>
      <p>The <strong>student visa process</strong> is straightforward if you prepare early and understand requirements.</p>
      <p>After graduation, your <strong>career trajectory depends on post-study work options</strong> like OPT and H-1B sponsorship.</p>
      <p>Policy changes (such as increased H-1B fees) add uncertainty, so PhD students should stay flexible and explore multiple pathways (academia, research labs, O-1 visas, or green cards via national interest waivers).</p>
    </div>
  `,
}


};

export { blogContent };
